import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { getOnThisDay, ACCENT_STYLES } from "@/lib/iconic-dates";
import { trackEvent } from "@/lib/analytics";

/**
 * OnThisDay — a "today in history" strip for the landing page.
 *
 * Shows 3–4 iconic dates whose month/day matches today (UTC). If no exact
 * matches, expands to same month, then to a ±3-week window. Each card
 * routes to `/history/{date}-{region}` (the SEO landing page → Cinema Mode
 * funnel).
 */
export default function OnThisDay() {
  const navigate = useNavigate();
  const { entries, mode, todayLabel } = useMemo(() => getOnThisDay(new Date(), 4), []);

  if (!entries || entries.length === 0) return null;

  const kicker =
    mode === "on_this_day" ? `On this day · ${todayLabel}`
    : mode === "this_month" ? `This month in history · ${todayLabel}`
    : `Around today · ${todayLabel}`;

  const headline =
    mode === "on_this_day" ? "Today in history."
    : mode === "this_month" ? "This month in history."
    : "Nearby moments from history.";

  const openEntry = (e) => {
    trackEvent("on_this_day_open", { date: e.date, region: e.region, mode });
    navigate(`/history/${e.date}-${e.region}`);
  };

  return (
    <section
      className="mx-auto max-w-6xl px-4 sm:px-8 mt-10 sm:mt-14"
      data-testid="on-this-day-section"
    >
      <div className="border-2 border-ink bg-paper-alt shadow-hard">
        {/* Header strip */}
        <div className="px-4 sm:px-8 py-3 border-b-2 border-ink flex items-center justify-between gap-3 bg-paper">
          <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-press-red flex items-center gap-2">
            <span className="animate-pulse text-press-red">◉</span> {kicker}
          </div>
          <div className="hidden md:block font-mono uppercase tracking-widest text-[10px] text-ink-muted">
            {entries.length} moment{entries.length === 1 ? "" : "s"} · click any to explore
          </div>
        </div>

        <div className="px-4 sm:px-8 pt-6 pb-2">
          <h2
            className="font-headline uppercase font-black leading-[0.95] tracking-tighter text-2xl sm:text-4xl lg:text-5xl"
            data-testid="on-this-day-headline"
          >
            {headline}
          </h2>
          <p className="mt-2 font-body italic text-sm sm:text-base text-ink-muted max-w-xl">
            Curated dates from around the world, sorted by how long ago they happened.
          </p>
        </div>

        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {entries.map((e, i) => {
            const style = ACCENT_STYLES[e.accent] || ACCENT_STYLES.amber;
            return (
              <button
                key={`${e.date}-${e.region}`}
                onClick={() => openEntry(e)}
                className="group relative text-left border-2 border-ink shadow-hard-sm hover:shadow-hard-hover overflow-hidden"
                style={{
                  background: style.bg,
                  color: style.text,
                  transition: "transform 150ms ease, box-shadow 150ms ease",
                }}
                data-testid={`on-this-day-card-${i}`}
              >
                {/* Accent stripe */}
                <div className="h-1.5 w-full" style={{ background: style.stripe }} />
                <div className="p-4 sm:p-5">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-headline font-black text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tighter">
                      {e.date.slice(0, 4)}
                    </div>
                    <span className="text-2xl sm:text-3xl" aria-hidden>{e.icon}</span>
                  </div>
                  <div className="mt-1 font-mono uppercase tracking-widest text-[10px] opacity-70">
                    {e.yearsAgo === 1 ? "1 year ago" : `${e.yearsAgo} years ago`} · {e.region}
                  </div>
                  <div className="mt-4 font-headline font-bold text-lg sm:text-xl leading-tight uppercase">
                    {e.label}
                  </div>
                  <div className="mt-2 font-body italic text-xs sm:text-sm leading-snug opacity-80 line-clamp-3">
                    {e.hook}
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] font-bold group-hover:gap-3 transition-all">
                    <Play size={11} /> Watch
                    <ArrowRight size={11} className="opacity-0 group-hover:opacity-100" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
